(() => {
  const popup = document.getElementById('lc-klaviyo-popup');
  if (!popup) return;

  // F·11 — defer entirely to LCOverlayController for the show gate.
  // Controller policies (consent resolved + session/scroll threshold +
  // 30-day TTL dismissal) are evaluated before the popup is allowed to
  // render. If the controller is missing for any reason, fall back to
  // the legacy behaviour rather than blocking the user.
  const controller = window.LCOverlayController;
  const listId = window.lcKlaviyoListId;
  const delay = window.lcKlaviyoPopupDelay ?? 8000;

  // A popup without a list id can never subscribe anyone - bail before
  // arming rather than POSTing g=undefined and failing generically
  // (mirrors the lc-subscribe.js missing-config guard).
  if (!listId) return;

  // F·15 — focus trap state. Saved across open/close so we can return
  // focus to the trigger element after dismissal.
  let lastFocused = null;

  // Journal articles (LOS7-3032): the 8s desktop timer and the mobile scroll
  // threshold both opened the popup mid-story. On the article template it
  // opens only once the end of the body is on screen, desktop and mobile
  // alike. Other templates keep the timer and the scroll threshold.
  const articleBody = document.body.classList.contains('template-article')
    ? document.getElementById('articleBody')
    : null;

  // Touch: never focus the email field on open. It raises the phone keyboard
  // over a dialog the reader has not chosen to use yet. Focus goes to the
  // dialog itself, and the Tab trap below still holds it there.
  const coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  // The end-of-body gate is consent and dismissal only. canShowNewsletter()
  // also wants 60% scroll or a second pageview, which a short article's body
  // end may never reach.
  function canOpenAtEnd() {
    return !controller || (controller.consentResolved() && !controller.isDismissed('newsletter'));
  }

  function open(gate) {
    // Final gate check at the moment of opening — the user may have
    // since dismissed it on a different tab.
    if (controller && !(gate ? gate() : controller.canShowNewsletter())) return;
    lastFocused = document.activeElement;
    popup.hidden = false;
    if (coarse) {
      popup.setAttribute('tabindex', '-1');
      popup.focus({ preventScroll: true });
    } else {
      document.getElementById('lc-kpop-email')?.focus();
    }
    document.addEventListener('keydown', handleKey);
    popup.addEventListener('keydown', trapTab);
  }

  function close() {
    if (popup.hidden) return;
    if (controller) {
      controller.markDismissed('newsletter');
    } else {
      try { localStorage.setItem('lc_dismissed_newsletter', String(Date.now())); } catch (_e) {}
    }
    document.removeEventListener('keydown', handleKey);
    popup.removeEventListener('keydown', trapTab);

    const restoreFocus = () => {
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus(); } catch (_e) {}
      }
    };
    const finish = () => {
      popup.hidden = true;
      popup.setAttribute('data-state', 'idle');
      restoreFocus();
    };

    // F·16 — animate the dialog out: the panel and its child close button
    // slide + fade out together, the backdrop fades, then we hide on
    // animationend. Reduced motion (or a missing panel) hides instantly.
    const panel = popup.querySelector('.lc-kpop__panel');
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !panel) { finish(); return; }

    popup.setAttribute('data-state', 'closing');
    let done = false;
    const onEnd = () => {
      if (done) return;
      done = true;
      panel.removeEventListener('animationend', onEnd);
      finish();
    };
    panel.addEventListener('animationend', onEnd);
    setTimeout(onEnd, 400); // fallback if animationend does not fire
  }

  function handleKey(e) {
    if (e.key === 'Escape') close();
  }

  function trapTab(e) {
    if (e.key !== 'Tab') return;
    const focusables = popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (document.activeElement === popup) {
      // Focus is on the dialog itself (touch open): step into it.
      (e.shiftKey ? last : first).focus();
      e.preventDefault();
    } else if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  }

  document.getElementById('lc-kpop-close')?.addEventListener('click', close);
  document.getElementById('lc-kpop-backdrop')?.addEventListener('click', close);

  function arm() {
    // Bail early if controller already says no (dismissed, etc.).
    if (controller && !controller.canShowNewsletter() && controller.isDismissed('newsletter')) {
      return;
    }
    if (articleBody && 'IntersectionObserver' in window) {
      // A span, not a div: base.css hides div:empty, and a display:none
      // target never intersects.
      const sentinel = document.createElement('span');
      sentinel.className = 'lc-kpop-sentinel';
      sentinel.style.display = 'block';
      sentinel.setAttribute('aria-hidden', 'true');
      articleBody.after(sentinel);
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        io.disconnect();
        open(canOpenAtEnd);
      });
      io.observe(sentinel);
      return;
    }
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (!isMobile) {
      setTimeout(open, delay);
      return;
    }
    const onScroll = () => {
      if (controller ? controller.canShowNewsletter() : window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) >= 0.5) {
        window.removeEventListener('scroll', onScroll);
        open();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  if (controller) {
    controller.onConsentResolved(arm);
  } else {
    arm();
  }

  // Subscribe form. The network call is delegated to the shared core
  // (assets/lc-klaviyo-subscribe.js), which fires the GA4 generate_lead bridge
  // with the 'Popup' source; this handler owns only the popup UI.
  document.getElementById('lc-kpop-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const emailInput = document.getElementById('lc-kpop-email');
    const errorEl = document.getElementById('lc-kpop-error');
    const submitBtn = e.target.querySelector('[type="submit"]');
    const email = emailInput?.value.trim();

    errorEl.hidden = true;
    errorEl.textContent = '';

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errorEl.textContent = 'Please enter a valid email address.';
      errorEl.hidden = false;
      emailInput?.focus();
      return;
    }

    submitBtn.disabled = true;

    try {
      await window.lcKlaviyoSubscribe({
        email,
        listId,
        companyId: window.lcKlaviyoCompanyId,
        source: 'Popup',
        customSource: 'Popup',
      });
      document.getElementById('lc-kpop-form').hidden = true;
      document.getElementById('lc-kpop-success').hidden = false;
      if (controller) {
        controller.markDismissed('newsletter');
      } else {
        try { localStorage.setItem('lc_dismissed_newsletter', String(Date.now())); } catch (_e) {}
      }
      setTimeout(close, 3000);
    } catch (_err) {
      errorEl.textContent = 'Something went wrong. Please try again.';
      errorEl.hidden = false;
      submitBtn.disabled = false;
    }
  });
})();
